import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActionSheetIOS,
  ActivityIndicator,
  Alert,
  AppState,
  Image,
  KeyboardAvoidingView,
  Linking,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import { Asset, launchCamera, launchImageLibrary } from 'react-native-image-picker';
import TabBar from '../../components/TabBar';
import { useChat } from '../../contexts/ChatContext';
import {
  v2DeleteSupportClientMessage,
  v2EditSupportClientMessage,
  v2GetSupportClientMessageHistory,
  v2GetSupportClientMessages,
  v2MarkSupportClientRead,
  v2SendSupportClientMessage,
  v2SendSupportClientMessageWithAttachment,
} from '../../services/v2/api';
import { COLORS } from '../../theme/themeTokens';
import { V2ChatMessage, V2ChatMessageHistoryItem, V2SupportClientSummary } from '../../types/v2';
import { getClientSupportMessageActions } from '../../tools/supportClient';
import SupportHeader from './SupportHeader';

const MESSAGE_MAX_LENGTH = 4000;
const FILE_MAX_SIZE = 10 * 1024 * 1024;
const TAB_BAR_HEIGHT = Platform.OS === 'ios' ? 65 : 80;

interface SelectedFile { uri: string; name: string; type: string }
interface MessageAction { label: string; run: () => void; destructive?: boolean }
interface Props {
  client: V2SupportClientSummary;
  onBack: () => void;
  onNavigate: (screen: string) => void;
}

const padDatePart = (value: number) => String(value).padStart(2, '0');
const messageTime = (value: string) => {
  const date = new Date(value);
  return `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};
const messageDate = (value: string) => {
  const date = new Date(value);
  return `${padDatePart(date.getDate())}.${padDatePart(date.getMonth() + 1)}.${date.getFullYear()}`;
};
const historyDateTime = (value: string) => {
  const date = new Date(value);
  return `${messageDate(value)} ${messageTime(value)}:${padDatePart(date.getSeconds())}`;
};
const sameDay = (first: string, second: string) => new Date(first).toDateString() === new Date(second).toDateString();

const SupportClientChatScreen: React.FC<Props> = ({ client, onBack, onNavigate }) => {
  const [messages, setMessages] = useState<V2ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState('');
  const [file, setFile] = useState<SelectedFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<V2ChatMessage | null>(null);
  const [editText, setEditText] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [actionMessage, setActionMessage] = useState<V2ChatMessage | null>(null);
  const [history, setHistory] = useState<V2ChatMessageHistoryItem[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const { refreshClientSupportUnreadCount } = useChat();

  const refresh = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const response = await v2GetSupportClientMessages(client.user_id);
      setMessages(response.messages || []);
      setError(null);
      await v2MarkSupportClientRead(client.user_id);
      await refreshClientSupportUnreadCount();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load messages');
    } finally {
      setLoading(false);
    }
  }, [client.user_id, refreshClientSupportUnreadCount]);

  useEffect(() => {
    void refresh(true);
    const timer = setInterval(() => { void refresh(false); }, 10000);
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active') void refresh(false);
    });
    return () => {
      clearInterval(timer);
      subscription.remove();
    };
  }, [refresh]);

  useEffect(() => {
    if (messages.length) setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 50);
  }, [messages.length]);

  const processAsset = (asset: Asset) => {
    if (!asset.uri) return;
    if (asset.fileSize && asset.fileSize > FILE_MAX_SIZE) {
      Alert.alert('File too large', 'Please select a file smaller than 10 MB.');
      return;
    }
    setFile({
      uri: asset.uri,
      name: asset.fileName || `attachment_${Date.now()}.jpg`,
      type: asset.type || 'image/jpeg',
    });
  };

  const pickCamera = async () => {
    const result = await launchCamera({ mediaType: 'mixed', quality: 0.8 });
    if (result.assets?.[0]) processAsset(result.assets[0]);
  };

  const pickLibrary = async () => {
    const result = await launchImageLibrary({ mediaType: 'mixed', quality: 0.8 });
    if (result.assets?.[0]) processAsset(result.assets[0]);
  };

  const chooseAttachment = () => {
    // The browser's own file picker already offers the library, the camera and files.
    if (Platform.OS === 'web') {
      void pickLibrary();
      return;
    }
    if (Platform.OS === 'ios') {
      ActionSheetIOS.showActionSheetWithOptions(
        { options: ['Cancel', 'Take Photo', 'Choose from Library'], cancelButtonIndex: 0 },
        index => {
          if (index === 1) void pickCamera();
          if (index === 2) void pickLibrary();
        },
      );
      return;
    }
    Alert.alert('Add Attachment', 'Choose an option', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Take Photo', onPress: () => void pickCamera() },
      { text: 'Choose from Library', onPress: () => void pickLibrary() },
    ]);
  };

  const send = async () => {
    const content = text.trim();
    if (sending || (!content && !file)) return;
    setSending(true);
    try {
      const created = file
        ? await v2SendSupportClientMessageWithAttachment(client.user_id, content || null, file)
        : await v2SendSupportClientMessage(client.user_id, content);
      setMessages(previous => [...previous, created]);
      setText('');
      setFile(null);
      setError(null);
      await refreshClientSupportUnreadCount();
    } catch (err) {
      Alert.alert('Message not sent', err instanceof Error ? err.message : 'Please try again.');
    } finally {
      setSending(false);
    }
  };

  const showHistory = async (message: V2ChatMessage) => {
    setHistory([]);
    setHistoryLoading(true);
    try {
      const response = await v2GetSupportClientMessageHistory(client.user_id, message.id);
      setHistory(response.items || []);
    } catch (err) {
      setHistory(null);
      Alert.alert('History unavailable', err instanceof Error ? err.message : 'Please try again.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const confirmDelete = (message: V2ChatMessage) => {
    Alert.alert('Delete message?', 'The client will no longer see this message.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            const updated = await v2DeleteSupportClientMessage(client.user_id, message.id);
            setMessages(previous => previous.map(item => item.id === message.id ? updated : item));
            await refreshClientSupportUnreadCount();
          } catch (err) {
            Alert.alert('Delete failed', err instanceof Error ? err.message : 'Please try again.');
          }
        },
      },
    ]);
  };

  const beginEdit = (message: V2ChatMessage) => {
    setEditing(message);
    setEditText(message.content || '');
  };

  const getMessageActions = (message: V2ChatMessage): MessageAction[] => {
    const available = getClientSupportMessageActions(message);
    const actions: MessageAction[] = [];
    if (available.includes('copy')) actions.push({ label: 'Copy', run: () => Clipboard.setString(message.content || '') });
    if (available.includes('edit')) {
      actions.push({ label: 'Edit', run: () => beginEdit(message) });
    }
    if (available.includes('delete')) {
      actions.push({ label: 'Delete', run: () => confirmDelete(message), destructive: true });
    }
    if (available.includes('history')) actions.push({ label: 'History', run: () => void showHistory(message) });
    return actions;
  };

  const openActions = (message: V2ChatMessage) => {
    const actions = getMessageActions(message);
    if (!actions.length) return;

    if (Platform.OS === 'ios') {
      const labels = [...actions.map(action => action.label), 'Cancel'];
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options: labels,
          cancelButtonIndex: labels.length - 1,
          destructiveButtonIndex: actions.findIndex(action => action.destructive),
        },
        index => actions[index]?.run(),
      );
      return;
    }
    setActionMessage(message);
  };

  const runMessageAction = (action: MessageAction) => {
    setActionMessage(null);
    setTimeout(action.run, 150);
  };

  const saveEdit = async () => {
    if (!editing) return;
    const content = editText.trim();
    if (!content || content === editing.content) {
      setEditing(null);
      return;
    }
    setEditSaving(true);
    try {
      const updated = await v2EditSupportClientMessage(client.user_id, editing.id, content);
      setMessages(previous => previous.map(item => item.id === editing.id ? updated : item));
      setEditing(null);
      await refreshClientSupportUnreadCount();
    } catch (err) {
      Alert.alert('Edit failed', err instanceof Error ? err.message : 'Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <View style={styles.container}>
      <SupportHeader title={client.user_login} subtitle="Client support" onBack={onBack} />
      <KeyboardAvoidingView style={styles.body} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {loading ? (
          <View style={styles.center}><ActivityIndicator color={COLORS.primaryNavy} /></View>
        ) : (
          <ScrollView
            ref={scrollRef}
            style={styles.messages}
            contentContainerStyle={styles.messagesContent}
            keyboardShouldPersistTaps="handled">
            {messages.length === 0 && (
              <View style={styles.empty}>
                <Text style={styles.emptyTitle}>New client conversation</Text>
                <Text style={styles.emptyText}>Send the first message to {client.user_login}.</Text>
              </View>
            )}
            {messages.map((message, index) => {
              const outgoing = message.sender_type === 'admin';
              const showDate = index === 0 || !sameDay(messages[index - 1].created_at, message.created_at);
              return (
                <React.Fragment key={message.id}>
                  {showDate && <Text style={styles.date}>{messageDate(message.created_at)}</Text>}
                  <View style={[styles.messageRow, outgoing ? styles.outgoingRow : styles.incomingRow]}>
                    <TouchableOpacity
                      activeOpacity={0.8}
                      delayLongPress={350}
                      onLongPress={() => openActions(message)}
                      style={[styles.bubble, outgoing ? styles.outgoingBubble : styles.incomingBubble]}>
                      {message.deleted_at ? (
                        <Text style={[styles.deleted, outgoing && styles.outgoingText]}>Message deleted</Text>
                      ) : (
                        <>
                          {!!message.content && <Text style={[styles.messageText, outgoing && styles.outgoingText]}>{message.content}</Text>}
                          {!!message.attachment_filename && (
                            <TouchableOpacity onPress={() => message.attachment_url && Linking.openURL(message.attachment_url)}>
                              <Text style={[styles.attachment, outgoing && styles.outgoingText]}>📎 {message.attachment_filename}</Text>
                            </TouchableOpacity>
                          )}
                        </>
                      )}
                      <Text style={[styles.time, outgoing && styles.outgoingTime]}>
                        {messageTime(message.created_at)}{message.edited_at ? ' · edited' : ''}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </React.Fragment>
              );
            })}
            {!!error && <Text style={styles.inlineError}>{error}</Text>}
          </ScrollView>
        )}

        {!!file && (
          <View style={styles.filePreview}>
            <Text style={styles.fileName} numberOfLines={1}>📎 {file.name}</Text>
            <TouchableOpacity onPress={() => setFile(null)}><Text style={styles.removeFile}>✕</Text></TouchableOpacity>
          </View>
        )}
        <View style={styles.composer}>
          <TouchableOpacity style={styles.attachButton} onPress={chooseAttachment} disabled={sending}>
            <Image source={require('../../assets/support-attach.png')} style={styles.attachIcon} />
          </TouchableOpacity>
          <TextInput
            value={text}
            onChangeText={value => setText(value.slice(0, MESSAGE_MAX_LENGTH))}
            style={styles.input}
            placeholder="Message"
            placeholderTextColor={COLORS.blueSteel}
            multiline
            // A <textarea> is two rows high by default, taller than the send button.
            rows={1}
            editable={!sending}
          />
          <TouchableOpacity style={styles.sendButton} onPress={() => void send()} disabled={sending || (!text.trim() && !file)}>
            {sending ? <ActivityIndicator size="small" color={COLORS.white} /> : <Image source={require('../../assets/support-send.png')} style={styles.sendIcon} />}
          </TouchableOpacity>
        </View>
        <View style={{ height: TAB_BAR_HEIGHT }} />
      </KeyboardAvoidingView>
      <TabBar activeTab="support" onNavigate={onNavigate} />

      <Modal
        visible={actionMessage !== null}
        transparent
        animationType="fade"
        onRequestClose={() => setActionMessage(null)}>
        <TouchableOpacity
          style={styles.actionOverlay}
          activeOpacity={1}
          onPress={() => setActionMessage(null)}>
          <View style={styles.actionCard} onStartShouldSetResponder={() => true}>
            <Text style={styles.actionTitle}>Message actions</Text>
            {actionMessage && getMessageActions(actionMessage).map(action => (
              <TouchableOpacity
                key={action.label}
                style={styles.actionRow}
                onPress={() => runMessageAction(action)}>
                <Text style={[styles.actionText, action.destructive && styles.actionDestructive]}>
                  {action.label}
                </Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity style={[styles.actionRow, styles.actionCancel]} onPress={() => setActionMessage(null)}>
              <Text style={styles.actionCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal visible={editing !== null} transparent animationType="fade" onRequestClose={() => !editSaving && setEditing(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>Edit message</Text>
            <TextInput value={editText} onChangeText={value => setEditText(value.slice(0, MESSAGE_MAX_LENGTH))} multiline style={styles.editInput} autoFocus />
            <View style={styles.modalActions}>
              <TouchableOpacity disabled={editSaving} onPress={() => setEditing(null)}><Text style={styles.cancel}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity disabled={editSaving || !editText.trim()} onPress={() => void saveEdit()}>
                {editSaving ? <ActivityIndicator size="small" color={COLORS.primaryNavy} /> : <Text style={styles.save}>Save</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <Modal visible={history !== null} transparent animationType="slide" onRequestClose={() => setHistory(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.historyCard}>
            <View style={styles.historyHeader}>
              <Text style={styles.modalTitle}>Message history</Text>
              <TouchableOpacity onPress={() => setHistory(null)}><Text style={styles.close}>✕</Text></TouchableOpacity>
            </View>
            {historyLoading ? <ActivityIndicator color={COLORS.primaryNavy} /> : (
              <ScrollView>
                {history?.length === 0 && <Text style={styles.emptyText}>No previous versions.</Text>}
                {history?.map(item => (
                  <View key={item.hist_id} style={styles.historyItem}>
                    <Text style={styles.historyMeta}>{item.operation === 'D' ? 'Deleted' : 'Edited'} · {historyDateTime(item.changed_at)}</Text>
                    <Text style={styles.historyText}>{item.content || 'No text content'}</Text>
                    {!!item.attachment_filename && <Text style={styles.historyAttachment}>📎 {item.attachment_filename}</Text>}
                  </View>
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: COLORS.pureLight },
  body: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  messages: { flex: 1 },
  messagesContent: { flexGrow: 1, paddingHorizontal: 12, paddingVertical: 14 },
  empty: { flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 240 },
  emptyTitle: { color: COLORS.primaryNavy, fontSize: 16, fontWeight: '600' },
  emptyText: { color: COLORS.blueSteel, textAlign: 'center', marginTop: 6 },
  date: { alignSelf: 'center', color: COLORS.blueSteel, fontSize: 11, marginVertical: 12 },
  messageRow: { width: '100%', marginVertical: 4 },
  outgoingRow: { alignItems: 'flex-end' },
  incomingRow: { alignItems: 'flex-start' },
  bubble: { maxWidth: '80%', minWidth: 86, borderRadius: 12, paddingHorizontal: 12, paddingVertical: 9 },
  outgoingBubble: { backgroundColor: COLORS.primaryNavy, borderBottomRightRadius: 4 },
  incomingBubble: { backgroundColor: COLORS.softGrey, borderBottomLeftRadius: 4 },
  messageText: { color: COLORS.primaryNavy, fontSize: 14, lineHeight: 19 },
  outgoingText: { color: COLORS.white },
  deleted: { color: COLORS.slate, fontSize: 13, fontStyle: 'italic' },
  attachment: { color: COLORS.primaryNavy, fontSize: 13, marginTop: 5, textDecorationLine: 'underline' },
  time: { alignSelf: 'flex-end', color: COLORS.blueSteel, fontSize: 9, marginTop: 5 },
  outgoingTime: { color: COLORS.softGrey },
  inlineError: { color: COLORS.brandOrange, textAlign: 'center', margin: 12 },
  actionOverlay: { flex: 1, backgroundColor: COLORS.overlay, justifyContent: 'center', paddingHorizontal: 36 },
  actionCard: { backgroundColor: COLORS.white, borderRadius: 12, overflow: 'hidden' },
  actionTitle: { color: COLORS.primaryNavy, fontSize: 17, fontWeight: '600', paddingHorizontal: 18, paddingVertical: 16 },
  actionRow: { minHeight: 48, justifyContent: 'center', paddingHorizontal: 18, borderTopWidth: 1, borderTopColor: COLORS.softGrey },
  actionText: { color: COLORS.primaryNavy, fontSize: 15 },
  actionDestructive: { color: COLORS.brandOrange },
  actionCancel: { alignItems: 'center' },
  actionCancelText: { color: COLORS.slate, fontSize: 15, fontWeight: '500' },
  filePreview: { marginHorizontal: 12, padding: 10, flexDirection: 'row', alignItems: 'center', backgroundColor: COLORS.white, borderWidth: 1, borderColor: COLORS.softGrey, borderRadius: 8 },
  fileName: { flex: 1, color: COLORS.primaryNavy },
  removeFile: { color: COLORS.brandOrange, paddingHorizontal: 8 },
  composer: { flexDirection: 'row', alignItems: 'flex-end', padding: 10, backgroundColor: COLORS.white, borderTopWidth: 1, borderTopColor: COLORS.softGrey },
  attachButton: { width: 36, height: 40, alignItems: 'center', justifyContent: 'center' },
  attachIcon: { width: 20, height: 20, tintColor: COLORS.blueSteel },
  input: { flex: 1, minHeight: 40, maxHeight: 110, borderWidth: 1, borderColor: COLORS.softGrey, borderRadius: 8, color: COLORS.primaryNavy, paddingHorizontal: 10, paddingVertical: 8 },
  sendButton: { width: 42, height: 40, marginLeft: 8, borderRadius: 8, backgroundColor: COLORS.primaryNavy, alignItems: 'center', justifyContent: 'center' },
  sendIcon: { width: 18, height: 18, tintColor: COLORS.white },
  modalOverlay: { flex: 1, backgroundColor: COLORS.overlay, justifyContent: 'center', padding: 22 },
  modalCard: { backgroundColor: COLORS.white, borderRadius: 12, padding: 18 },
  modalTitle: { color: COLORS.primaryNavy, fontSize: 18, fontWeight: '600' },
  editInput: { minHeight: 100, maxHeight: 220, marginTop: 14, borderWidth: 1, borderColor: COLORS.borderMuted, borderRadius: 8, padding: 10, color: COLORS.primaryNavy, textAlignVertical: 'top' },
  modalActions: { flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 24, marginTop: 16 },
  cancel: { color: COLORS.slate, fontSize: 15 },
  save: { color: COLORS.primaryNavy, fontSize: 15, fontWeight: '600' },
  historyCard: { maxHeight: '75%', backgroundColor: COLORS.white, borderRadius: 14, padding: 18 },
  historyHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  close: { color: COLORS.slate, fontSize: 20, padding: 6 },
  historyItem: { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: COLORS.softGrey },
  historyMeta: { color: COLORS.blueSteel, fontSize: 11, marginBottom: 6 },
  historyText: { color: COLORS.primaryNavy, fontSize: 14, lineHeight: 19 },
  historyAttachment: { color: COLORS.slate, fontSize: 12, marginTop: 5 },
});

export default SupportClientChatScreen;
